import { useGetIdentity, useLogout } from "@refinedev/core";
import { useNavigate } from "react-router";
import { Avatar, Button, Dropdown, Grid, Tooltip, type MenuProps } from "antd";
import {
  MenuOutlined,
  SearchOutlined,
  PlusOutlined,
  LogoutOutlined,
  SettingOutlined,
  ExperimentOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  AccountBookOutlined,
  WalletOutlined,
  SoundOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { useEdel } from "../contexts/color-mode";
import { useNav } from "../components/app-sider/nav-context";
import { getInitials } from "../utils";
import { openCommandPalette } from "./palette-store";
import { useLabs } from "./flags";
import { openPaymentWizard } from "../pages/finans/payment-store";

type Identity = { id: string; name?: string; email?: string; avatar?: string };

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Güneş/ay ikonu (currentColor) */
const ThemeIcon = ({ dark }: { dark: boolean }) =>
  dark ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" fill="currentColor" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="4.2" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
      </g>
    </svg>
  );

/** Üst bar: kişi arama (⌘K), "+ Yeni", tema, kullanıcı menüsü. */
export const TopBar = () => {
  const navigate = useNavigate();
  const { mode, setMode, palette } = useEdel();
  const { setMobileOpen } = useNav();
  const { data: me } = useGetIdentity<Identity>();
  const { mutate: logout } = useLogout();
  const [labs, setLabs] = useLabs();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;
  const isDark = mode === "dark";
  const name = me?.name || me?.email || "";

  const createMenu: MenuProps = {
    items: [
      { key: "lead", icon: <UserAddOutlined />, label: "Kişi ekle", onClick: () => navigate("/kisiler?yeni=1") },
      { key: "user", icon: <UsergroupAddOutlined />, label: "Öğrenci / eğitmen hesabı aç", onClick: () => navigate("/profiles?yeni=1") },
      { key: "appt", icon: <CalendarOutlined />, label: "Ders planla", onClick: () => navigate("/appointments") },
      { key: "task", icon: <CheckSquareOutlined />, label: "Görev ekle", onClick: () => navigate("/gorevler?yeni=1") },
      { key: "pay", icon: <WalletOutlined />, label: "Ödeme al", onClick: () => openPaymentWizard() },
      { key: "fin", icon: <AccountBookOutlined />, label: "Gider ekle", onClick: () => navigate("/finans/gider?yeni=1") },
      { key: "ann", icon: <SoundOutlined />, label: "Duyuru gönder", onClick: () => navigate("/announcements?yeni=1") },
    ],
  };

  const userMenu: MenuProps = {
    items: [
      { key: "settings", icon: <SettingOutlined />, label: "Ayarlar", onClick: () => navigate("/ayarlar") },
      ...(labs
        ? [{ key: "labs", icon: <ExperimentOutlined />, label: "Labs'ı kapat", onClick: () => setLabs(false) }]
        : []),
      { type: "divider" as const },
      { key: "logout", icon: <LogoutOutlined />, label: "Çıkış yap", danger: true, onClick: () => logout() },
    ],
  };

  return (
    <header className="es-top">
      {isMobile && (
        <Button
          type="text"
          shape="circle"
          aria-label="Menüyü aç"
          icon={<MenuOutlined style={{ fontSize: 18 }} />}
          onClick={() => setMobileOpen(true)}
        />
      )}
      <button type="button" className="es-search" onClick={openCommandPalette} aria-label="Kişi ara ya da komut çalıştır">
        <SearchOutlined />
        <span className="es-search__text">Kişi ara ya da ne yapmak istediğini yaz…</span>
        <span className="es-kbd">{isMac ? "⌘K" : "Ctrl K"}</span>
      </button>
      <span className="es-top__spacer" />
      <Dropdown menu={createMenu} trigger={["click"]} placement="bottomRight">
        <Button icon={<PlusOutlined />} aria-label="Yeni oluştur">
          {!isMobile && "Yeni"}
        </Button>
      </Dropdown>
      <Tooltip title={isDark ? "Açık temaya geç" : "Koyu temaya geç"}>
        <Button
          type="text"
          shape="circle"
          aria-label="Tema değiştir"
          onClick={() => setMode()}
          icon={
            <span style={{ color: palette.primary, display: "inline-flex" }}>
              <ThemeIcon dark={isDark} />
            </span>
          }
        />
      </Tooltip>
      <Dropdown menu={userMenu} trigger={["click"]} placement="bottomRight">
        <button type="button" className="es-user" aria-label="Hesap menüsü">
          <Avatar
            size={32}
            src={me?.avatar || undefined}
            style={{ background: palette.primary, color: "#fff", fontWeight: 700 }}
          >
            {getInitials(name)}
          </Avatar>
          {name && <span className="es-user__name">{name}</span>}
          <DownOutlined style={{ fontSize: 10, color: palette.textSoft }} />
        </button>
      </Dropdown>
    </header>
  );
};
